// The single shared USD formatter (ALL-5638). Every USD figure the user sees goes
// through `formatUsd` so that decimals, separators, the sign position and the
// unknown-value dash are decided in exactly one place.

/** Shown for a value we do not have. Never a zero, never a guess. */
export const USD_UNKNOWN_LABEL = "—";

const usd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Format a USD amount for display: two decimals, thousands separators, and a
 * leading minus for negatives (`-$1.00`).
 *
 * Anything unknown renders as an em dash — `null`, `undefined`, a blank string,
 * a non-numeric string and a non-finite number all return `USD_UNKNOWN_LABEL`.
 * A missing value must never be shown as `$0.00`.
 */
export function formatUsd(value: number | string | null): string {
  const parsed = parseUsd(value);
  return parsed === null ? USD_UNKNOWN_LABEL : usd.format(parsed);
}

function parseUsd(value: number | string | null): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value !== "string") {
    return null;
  }
  // Number("") and Number("   ") are both 0, so guard the blank cases first:
  // an absent value must not be rendered as a real $0.00.
  const trimmed = value.trim();
  if (trimmed === "") {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}
